import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, CalendarBlank, Fire, MapPinLine, Heart, ShareNetwork, Star } from "@phosphor-icons/react";
import { SmartImage } from "./shared";
import ShareMenu from "./ShareMenu";
import { money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSocial } from "../context/SocialContext";
import { toast } from "sonner";

function fmtDate(d) {
  if (!d) return "Date TBA";
  try {
    return new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  } catch { return d; }
}

const MODE_BADGE = { rsvp: "RSVP", approval: "Approval" };

export default function EventCard({ event, ratio = "poster", featured = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const social = useSocial();
  const dates = event.dates && event.dates.length ? event.dates : (event.date ? [event.date] : []);
  const cities = event.cities && event.cities.length ? event.cities : (event.city ? [event.city] : []);
  const dateLabel = dates.length > 1 ? `${fmtDate(dates[0])} +${dates.length - 1}` : fmtDate(dates[0]);
  const cityLabel = cities.length > 1 ? `${cities.length} cities` : (cities[0] || "—");
  const specialMode = (event.modes || []).find((m) => m !== "public");
  const liked = social?.isLiked(event.id);
  const shareUrl = `${window.location.origin}/events/${event.id}`;

  const onLike = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user || user.role !== "customer") { toast.info("Log in as a customer to like events."); navigate("/login"); return; }
    try { await social.toggleLike(event.id); } catch { toast.error("Couldn't update like."); }
  };

  return (
    <Link to={`/events/${event.id}`} data-testid={`event-card-${event.id}`} className="group block card-lift">
      <div className="relative">
        <SmartImage src={event.banner} alt={event.title} ratio={featured ? "video" : ratio} />
        {event.is_featured && (
          <span className="absolute top-3 left-3 z-[2] inline-flex items-center gap-1 px-2.5 py-1 rounded-full glass-dark text-white text-xs font-semibold">
            <Fire weight="fill" size={13} className="text-primary" /> Featured
          </span>
        )}
        {specialMode && MODE_BADGE[specialMode] && (
          <span className="absolute bottom-3 left-3 z-[2] px-2.5 py-1 rounded-full bg-navy text-white text-[10px] font-bold uppercase tracking-wide">
            {MODE_BADGE[specialMode]}
          </span>
        )}
        <div className="absolute top-3 right-3 z-[3] flex items-center gap-1.5">
          <button onClick={onLike} data-testid={`card-like-${event.id}`} aria-label="Like"
            className="w-8 h-8 grid place-items-center rounded-full glass-dark text-white hover:scale-110 transition-transform">
            <Heart size={16} weight={liked ? "fill" : "regular"} className={liked ? "text-primary" : "text-white"} />
          </button>
          <ShareMenu url={shareUrl} title={event.title} text={`Check out ${event.title} on EventVexa`} testid={`card-share-${event.id}`}
            trigger={
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} data-testid={`card-share-btn-${event.id}`} aria-label="Share"
                className="w-8 h-8 grid place-items-center rounded-full glass-dark text-white hover:scale-110 transition-transform">
                <ShareNetwork size={16} />
              </button>
            } />
        </div>
      </div>
      <div className="pt-3">
        <h3 className="font-heading font-semibold text-base md:text-lg tracking-tight leading-snug line-clamp-2 group-hover:text-primary transition-colors">
          {event.title}
        </h3>
        <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><CalendarBlank size={13} /> {dateLabel}</span>
          <span className="inline-flex items-center gap-1 truncate">
            {cities.length > 1 ? <MapPinLine size={13} /> : <MapPin size={13} />} {cityLabel}
          </span>
        </div>
        <div className="mt-2 flex items-center justify-between text-sm">
          {event.is_free ? (
            <span className="font-semibold text-emerald-700">Free</span>
          ) : (
            <span className="font-semibold">{money(event.min_price)} <span className="text-muted-foreground font-normal text-xs">onwards</span></span>
          )}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {event.rating_count > 0 && (
              <span className="inline-flex items-center gap-0.5"><Star size={12} weight="fill" className="text-amber-500" /> {event.rating} ({event.rating_count})</span>
            )}
            {event.occurrence_count > 1 && <span>{event.occurrence_count} shows</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}
